/** A project's run history, filterable by script and status. */

import { Link, useSearchParams } from "react-router-dom";

import { endpoints } from "@/api/endpoints";
import type { RunStatus } from "@/api/endpoints";
import { useProject } from "@/components/ProjectLayout";
import {
  Empty,
  ErrorState,
  Loading,
  RelativeTime,
  Status,
  formatDuration,
  isTerminal,
} from "@/components/ui";
import { useAsync } from "@/hooks/useAsync";

const STATUSES: RunStatus[] = [
  "running",
  "queued",
  "succeeded",
  "succeeded_with_warnings",
  "validation_failed",
  "failed",
  "timed_out",
  "cancelled",
  "skipped",
];

const PAGE_SIZE = 50;

export function Runs() {
  const { project } = useProject();
  const [params, setParams] = useSearchParams();

  const scriptRef = params.get("script_ref") ?? undefined;
  const status = params.get("status") ?? undefined;
  const offset = Number(params.get("offset") ?? 0);

  const { data, error, loading, reload } = useAsync(
    () =>
      endpoints.runs(project.slug, {
        script_ref: scriptRef,
        status,
        limit: PAGE_SIZE,
        offset,
      }),
    [project.slug, scriptRef, status, offset],
    // Anything in flight changes on its own, so the list refreshes while it is open.
    { pollMs: 10_000 },
  );

  const setParam = (key: string, value: string | undefined) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete("offset");
    setParams(next);
  };

  if (loading && !data) return <Loading rows={5} label="Loading runs" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const runs = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="stack">
      <div className="row" style={{ gap: 10 }}>
        <select
          className="select"
          style={{ width: "auto" }}
          value={status ?? ""}
          onChange={(event) => setParam("status", event.target.value || undefined)}
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          {STATUSES.map((value) => (
            <option key={value} value={value}>
              {value.replace(/_/g, " ")}
            </option>
          ))}
        </select>

        {scriptRef && (
          <button
            type="button"
            className="button button--small"
            onClick={() => setParam("script_ref", undefined)}
          >
            Clear script filter: {scriptRef}
          </button>
        )}

        <div style={{ flex: 1 }} />
        <span className="muted" style={{ fontSize: 12.5 }}>
          {total} {total === 1 ? "run" : "runs"}
        </span>
      </div>

      {runs.length === 0 ? (
        <div className="card">
          <Empty title="No runs yet">
            Runs appear here as soon as someone triggers a script.
          </Empty>
        </div>
      ) : (
        <div className="card">
          <div className="table__scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Run</th>
                  <th>Script</th>
                  <th>Status</th>
                  <th>Triggered by</th>
                  <th>Duration</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.id}>
                    <td>
                      <Link to={run.id} style={{ fontWeight: 550 }}>
                        #{run.number}
                      </Link>
                    </td>
                    <td className="truncate" style={{ maxWidth: 200 }}>
                      {run.script_name}
                    </td>
                    <td>
                      <Status status={run.status} />
                    </td>
                    <td className="muted truncate" style={{ maxWidth: 200 }}>
                      {run.triggered_by_email ?? (
                        <span className="faint">{run.trigger}</span>
                      )}
                    </td>
                    <td className="muted mono">
                      {isTerminal(run.status) ? formatDuration(run.duration_ms) : "—"}
                    </td>
                    <td className="muted">
                      <RelativeTime iso={run.created_at} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="row row--between">
          <button
            type="button"
            className="button button--small"
            disabled={offset === 0}
            onClick={() => setParams({ ...Object.fromEntries(params), offset: String(Math.max(0, offset - PAGE_SIZE)) })}
          >
            Previous
          </button>
          <span className="muted" style={{ fontSize: 12.5 }}>
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <button
            type="button"
            className="button button--small"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setParams({ ...Object.fromEntries(params), offset: String(offset + PAGE_SIZE) })}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
